import React, { useEffect, useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    Checkbox,
    ListItemText,
    Divider,
    FormControlLabel,
    FormHelperText,
    InputAdornment
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Info';
import CurrencyInput from '../../common/CurrencyInput';
import { getLocationItem } from '../../../collums-constants/utils';

function PriceAndLocationTab({
    classes,
    register,
    clinics,
    control,
    watch,
    validationMessages,
    setValue,
    selectedMembership,
    membershipTaxes,
    isFromOrg,
    clinic,
    allClinics,
    errors,
    suggestedPrice
}) {
    const taxWatch = watch('tax');
    const formNetPrice = watch('netPrice');
    const formSalePrice = watch('salePrice');
    const servicesWatch = watch('services');

    const getMostCommonTax = currentServices => {
        if (!currentServices || currentServices.length === 0 || !membershipTaxes?.length) return null;

        const taxCounts = {};
        let firstTaxId = null;

        currentServices.forEach(svc => {
            const taxId = (svc.service?.taxType || svc.service?.itemTax?.id || '')?.toString?.();
            if (!taxId) return;
            if (!firstTaxId) firstTaxId = taxId;
            taxCounts[taxId] = (taxCounts[taxId] || 0) + 1;
        });

        if (!firstTaxId) return null;

        let maxCount = 0;
        let mostCommonTaxId = firstTaxId;
        for (const [taxId, count] of Object.entries(taxCounts)) {
            if (count > maxCount) {
                maxCount = count;
                mostCommonTaxId = taxId;
            }
        }

        return membershipTaxes.find(t => t.id?.toString() === mostCommonTaxId) || null;
    };

    const isInitialServicesLoad = useRef(!!selectedMembership?.id);
    const taxOnChangeRef = useRef(null);
    const [netPrice, setNetPrice] = useState(
        isFromOrg ? selectedMembership?.netPrice : getLocationItem(selectedMembership, clinic).netPrice
    );
    const [salePrice, setSalePrice] = useState(
        isFromOrg ? selectedMembership?.salePrice : getLocationItem(selectedMembership, clinic).salePrice
    );
    const [discount, setDiscount] = useState(
        isFromOrg ? selectedMembership?.discount || 0 : getLocationItem(selectedMembership, clinic).discount || 0
    );

    const isSyncingRef = useRef(false);

    const listOptions =
        clinics &&
        clinics.map(clinicItem => {
            let isSelected = false;
            if (!selectedMembership?.id) {
                if (clinics.length === 1) {
                    isSelected = true;
                }
            }
            if (selectedMembership?.id && selectedMembership.locations) {
                isSelected = selectedMembership.locations.some(loc => loc.clinic === clinicItem.id);
            }
            return {
                value: clinicItem.id,
                label: clinicItem.accountName,
                subtitle: undefined,
                isSelected
            };
        });

    const calculate = (event, isNetPrice) => {
        let value = event.target.value;
        const tax = taxWatch?.rate || 0;

        value = value.replace(/[£$€]/g, '');
        value = value.replace(',', '');

        let newGrossValue;
        if (isNetPrice) {
            newGrossValue = (parseFloat(value) * tax) / 100 + parseFloat(value);
            newGrossValue ? setSalePrice(newGrossValue) : setSalePrice(0);
            setNetPrice(value);
        } else {
            newGrossValue = parseFloat(value) || 0;
            const newNetValue = parseFloat(value) - (parseFloat(value) * tax) / (100 + tax);
            newNetValue ? setNetPrice(Math.round(newNetValue * 100) / 100) : setNetPrice(0);
            setSalePrice(value);
        }

        // Update discount based on new gross price
        if (suggestedPrice > 0) {
            const newDiscount = (1 - (newGrossValue || 0) / suggestedPrice) * 100;
            setDiscount(Math.round(Math.max(0, Math.min(100, newDiscount)) * 100) / 100);
        }
    };

    const handleDiscountChange = event => {
        const value = parseFloat(event.target.value) || 0;
        // Clamp between 0 and 100
        const discountValue = Math.min(100, Math.max(0, value));
        setDiscount(discountValue);

        // Recalculate prices based on percentage discount
        const newSalePrice = suggestedPrice * (1 - discountValue / 100);
        const tax = taxWatch?.rate || 0;
        const newNetPrice = newSalePrice / (1 + tax / 100);

        setSalePrice(Math.round(newSalePrice * 100) / 100);
        setNetPrice(Math.round(newNetPrice * 100) / 100);
    };

    useEffect(() => {
        register('salePrice', salePrice);
        register('netPrice', netPrice);
        register('discount', discount);
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setValue('discount', discount);
        // eslint-disable-next-line
    }, [discount]);

    useEffect(() => {
        const tax = taxWatch?.rate || 0;
        if (salePrice) {
            const newNetPrice = parseFloat(salePrice) / (1 + tax / 100);
            setNetPrice(Math.round(newNetPrice * 100) / 100);
        }
        // eslint-disable-next-line
    }, [taxWatch]);

    useEffect(() => {
        if (!netPrice && salePrice) {
            const tax = taxWatch?.rate || 0;
            setNetPrice(parseFloat(salePrice) - (parseFloat(salePrice) * tax) / (100 + tax));
        }
        // eslint-disable-next-line
    }, []);

    // Sync local state to form (when user edits price inputs)
    useEffect(() => {
        if (isSyncingRef.current) return;
        const formNet = parseFloat(formNetPrice) || 0;
        const formSale = parseFloat(formSalePrice) || 0;
        const localNet = parseFloat(netPrice) || 0;
        const localSale = parseFloat(salePrice) || 0;

        if (Math.abs(formNet - localNet) > 0.001 || Math.abs(formSale - localSale) > 0.001) {
            isSyncingRef.current = true;
            setValue('salePrice', salePrice);
            setValue('netPrice', netPrice);
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 0);
        }
        // eslint-disable-next-line
    }, [netPrice, salePrice]);

    // Sync form to local state (when services change and price is auto-calculated)
    useEffect(() => {
        if (isSyncingRef.current) return;
        const formNet = parseFloat(formNetPrice) || 0;
        const formSale = parseFloat(formSalePrice) || 0;
        const localNet = parseFloat(netPrice) || 0;
        const localSale = parseFloat(salePrice) || 0;

        if (Math.abs(formNet - localNet) > 0.001 || Math.abs(formSale - localSale) > 0.001) {
            isSyncingRef.current = true;
            setNetPrice(formNet);
            setSalePrice(formSale);
            setTimeout(() => {
                isSyncingRef.current = false;
            }, 0);
        }
        // eslint-disable-next-line
    }, [formNetPrice, formSalePrice]);

    // Auto-set tax when services change
    useEffect(() => {
        if (isInitialServicesLoad.current) {
            isInitialServicesLoad.current = false;
            return;
        }

        const mostCommonTax = getMostCommonTax(servicesWatch);
        if (mostCommonTax && taxOnChangeRef.current) {
            taxOnChangeRef.current(mostCommonTax);
        }
        // eslint-disable-next-line
    }, [JSON.stringify(servicesWatch)]);

    const getSelectedValues = (options, selectedLocations) => {
        return options.map(option => {
            const foundValue = selectedLocations.find(location => option.value === location.value);
            if (foundValue) return { ...option, isSelected: true };
            else return option;
        });
    };

    return (
        <>
            <div className={classes.formContent}>
                <div className={classes.formContentRow}>
                    <Typography className={classes.spanLocationLabel}>
                        This membership will be available to book in the locations that offer this service.
                    </Typography>
                </div>
                {suggestedPrice > 0 && (
                    <div className={classes.formContentRow} style={{ marginBottom: 16 }}>
                        <Typography style={{ fontSize: '14px' }}>
                            Full price of services selected = £{suggestedPrice.toFixed(2)} pm
                        </Typography>
                    </div>
                )}
                <div className={classes.formContentRow}>
                    <FormControl className={classes.priceFormItem} variant="outlined" error={!!errors?.tax}>
                        <InputLabel>Tax *</InputLabel>
                        <Controller
                            name="tax"
                            rules={register({ required: validationMessages.required })}
                            defaultValue={
                                isFromOrg || allClinics.length === 1
                                    ? selectedMembership.itemTax
                                    : getLocationItem(selectedMembership, clinic)?.itemTax
                            }
                            control={control}
                            render={({ value, onChange: controllerOnChange, ...props }) => {
                                taxOnChangeRef.current = controllerOnChange;
                                return (
                                    <Select
                                        {...props}
                                        style={{ width: '100%' }}
                                        value={value?.id || ''}
                                        error={!!errors?.tax}
                                        onChange={e => {
                                            const selected = membershipTaxes.find(tax => tax.id === e.target.value);
                                            controllerOnChange(selected);
                                        }}
                                    >
                                        {membershipTaxes &&
                                            membershipTaxes.map(option => (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.rate}%
                                                </MenuItem>
                                            ))}
                                    </Select>
                                );
                            }}
                        />
                        {errors?.tax && <FormHelperText error>{errors.tax.message}</FormHelperText>}
                    </FormControl>
                </div>
                {suggestedPrice > 0 && (
                    <div className={classes.formContentRow}>
                        <FormControl className={classes.priceFormItem}>
                            <InputLabel shrink>Discount</InputLabel>
                            <div style={{ height: 8 }} />
                            <TextField
                                type="number"
                                variant="outlined"
                                size="small"
                                value={discount}
                                onChange={handleDiscountChange}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    inputProps: { min: 0, max: 100, step: 'any' }
                                }}
                            />
                        </FormControl>
                    </div>
                )}
                <div className={classes.formContentRow}>
                    <FormControl className={classes.priceFormItem}>
                        <InputLabel shrink required>
                            Net Price
                        </InputLabel>
                        <div style={{ height: 8 }} />
                        <CurrencyInput
                            disabled={taxWatch === undefined}
                            value={netPrice * 100}
                            CustomInput={TextField}
                            onChange={e => {
                                calculate(e, true);
                            }}
                        />
                    </FormControl>

                    <FormControl className={classes.priceFormItem}>
                        <InputLabel shrink required>
                            Gross Price
                        </InputLabel>
                        <div style={{ height: 8 }} />
                        <CurrencyInput
                            disabled={taxWatch === undefined}
                            value={salePrice * 100}
                            CustomInput={TextField}
                            onChange={e => {
                                calculate(e, false);
                            }}
                        />
                    </FormControl>
                </div>
                <div
                    className={classes.formContentRow}
                    style={{ display: isFromOrg && allClinics.length > 1 ? '' : 'none' }}
                >
                    <div className={classes.locationsTitle}>
                        <b>{'Locations'}</b>
                        {clinics.length === 1 && (
                            <Tooltip
                                title={
                                    <Typography style={{ fontSize: 14 }}>
                                        Select the locations that can book this service.
                                    </Typography>
                                }
                                aria-label="help"
                            >
                                <HelpIcon style={{ fontSize: 18 }} />
                            </Tooltip>
                        )}
                    </div>
                    <Controller
                        name={'locations'}
                        control={control}
                        defaultValue={
                            selectedMembership.locations
                                ? getSelectedValues(listOptions, selectedMembership.locations)
                                : listOptions
                        }
                        rules={register}
                        render={({ onChange, value }) => {
                            const checkAll = (e, checked) => {
                                const newItems = [
                                    ...value.map(item => {
                                        item.isSelected = checked;
                                        return item;
                                    })
                                ];
                                onChange(newItems);
                            };

                            const handleToggle = async option => {
                                const currentIndex = ((value.length && value) || listOptions || []).findIndex(
                                    v => v.value === option.value
                                );
                                const newItems = value.length ? [...value] : [...listOptions];
                                let newItem;
                                if (value.length) {
                                    newItem = {
                                        ...value[currentIndex],
                                        isSelected: !value[currentIndex].isSelected
                                    };
                                } else {
                                    newItem = {
                                        ...listOptions[currentIndex],
                                        isSelected: !listOptions[currentIndex].isSelected
                                    };
                                }
                                newItems.splice(currentIndex, 1, newItem);
                                onChange(newItems);
                            };
                            return (
                                <>
                                    {isFromOrg ||
                                        (allClinics.length === 1 && value.length > 1 && (
                                            <div className={classes.locationFilter}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            onChange={checkAll}
                                                            edge="start"
                                                            tabIndex={-1}
                                                            disableRipple
                                                        />
                                                    }
                                                    label="Select all"
                                                />
                                            </div>
                                        ))}
                                    <List>
                                        {((value.length && value) || listOptions).map((option, index) => (
                                            <React.Fragment key={index}>
                                                <ListItem role={undefined} dense button>
                                                    <ListItemIcon>
                                                        <Checkbox
                                                            onClick={() => {
                                                                handleToggle(option);
                                                            }}
                                                            disabled={
                                                                listOptions?.length === 1 && listOptions[0].isSelected
                                                            }
                                                            edge="start"
                                                            checked={option.isSelected}
                                                            tabIndex={-1}
                                                            disableRipple
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText primary={option.label} secondary={option.subtitle} />
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </>
                            );
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export default PriceAndLocationTab;
